import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigationType, useSearchParams } from 'react-router-dom';

import { deepEqual, iaRa } from 'lib/helpers/utility';
import { useFilters } from 'lib/hooks/queries/filters/useFilters';

import { applyFilterHandler } from 'components/shared/Filter/lib/helpers/handleAppliedFilter';

import {
  getUrlFiltersQuery,
  isSameUrlFilters,
  parseUrlFilters,
  replaceUrlFilters,
  serializeUrlFilters
} from '../lib/urlFilters';

import { useFilterLabelHydration } from './useFilterLabelHydration';

const TABLE_STATE_PARAMS = ['page', 'sort', 'order'];

export const useReactFilters = ({ name, defaultFilters, paramPrefix = name, syncToUrl = true }) => {
  const [filters, setFilters] = useState(null);
  const [initialFilters, setInitialFilters] = useState(null);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [isEditingFilter, setIsEditingFilter] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigationType = useNavigationType();
  const view = searchParams.get('view');
  const urlFiltersQuery = syncToUrl ? getUrlFiltersQuery(searchParams, paramPrefix) : null;
  const appliedViewRef = useRef(view);

  const tableStateParamKeys = TABLE_STATE_PARAMS.map((key) =>
    paramPrefix ? `${paramPrefix}_${key}` : key
  );
  const hasTableStateParams = tableStateParamKeys.some((key) => searchParams.has(key));

  const withoutTableStateParams = (prev) => {
    const newParams = new URLSearchParams(prev);
    tableStateParamKeys.forEach((key) => newParams.delete(key));
    return newParams;
  };

  const { data: filtersData, isFetched } = useFilters({
    params: { category: name },
    dependencies: [name],
    options: { enabled: Boolean(name?.length) }
  });

  useFilterLabelHydration({ filters, setFilters, defaultFilters });

  const appliedFilters = useCallback(
    ({ ignoreUrl = false } = {}) => {
      if (!isFetched) return;

      const urlFilters = ignoreUrl
        ? null
        : parseUrlFilters(urlFiltersQuery, defaultFilters, paramPrefix);
      let baseFilter = null;

      if (view && filtersData?.filters) {
        baseFilter = iaRa(filtersData.filters).find((f) => f.name === view) || null;
        if (baseFilter?.default && hasTableStateParams) {
          setSearchParams(withoutTableStateParams, { replace: true, state: location.state });
        }
      } else if (
        !urlFilters &&
        filtersData?.default?.filters &&
        !location?.state?.disableDefaultFilter
      ) {
        baseFilter = filtersData.default;

        setSearchParams(
          (prev) => {
            const newParams = withoutTableStateParams(prev);
            newParams.set('view', filtersData.default.name);
            return newParams;
          },
          { replace: true, state: location.state }
        );
      }

      const appliedFilters = applyFilterHandler(defaultFilters, urlFilters || baseFilter);
      const appliedInitialFilters = applyFilterHandler(defaultFilters, baseFilter);

      appliedViewRef.current = view;
      setFilters(appliedFilters);
      setInitialFilters(appliedInitialFilters);
      setIsEditingFilter(
        !!view && !isSameUrlFilters(appliedInitialFilters, appliedFilters, defaultFilters)
      );
      setFiltersInitialized(true);
      return appliedFilters;
    },
    [isFetched, filtersInitialized, name]
  );

  useEffect(() => {
    appliedFilters();
    const onNavLinkClicked = () => appliedFilters({ ignoreUrl: true });
    window.addEventListener('nav-link-clicked', onNavLinkClicked);
    return () => {
      window.removeEventListener('nav-link-clicked', onNavLinkClicked);
    };
  }, [appliedFilters]);

  useEffect(() => {
    if (!filtersInitialized || !filtersData?.filters) return;

    const isSameView = appliedViewRef.current === view;
    appliedViewRef.current = view;
    if (!view) return;

    const selectedFilter = iaRa(filtersData.filters).find((f) => f.name === view);
    const applied = applyFilterHandler(defaultFilters, selectedFilter || null);

    if (isSameView && urlFiltersQuery) {
      setInitialFilters(applied);
      setIsEditingFilter(!isSameUrlFilters(applied, filters, defaultFilters));
      return;
    }

    const isSameFilters = deepEqual(filters, applied);
    if (!isSameFilters) {
      setFilters(applied);
      setInitialFilters(applied);
    }
  }, [view, filtersData, filtersInitialized]);

  useEffect(() => {
    const appliesUrl =
      navigationType === 'POP' || (navigationType === 'PUSH' && !!location.state?.applyUrlFilters);
    if (!syncToUrl || !filtersInitialized || !appliesUrl) return;

    const savedView = view ? iaRa(filtersData?.filters).find((f) => f.name === view) || null : null;
    const urlFilters = parseUrlFilters(urlFiltersQuery, defaultFilters, paramPrefix);
    const baseFilters = applyFilterHandler(defaultFilters, savedView);
    const nextFilters = applyFilterHandler(defaultFilters, urlFilters || savedView);

    appliedViewRef.current = view;
    setInitialFilters(baseFilters);
    setFilters(nextFilters);
    setIsEditingFilter(!!view && !isSameUrlFilters(baseFilters, nextFilters, defaultFilters));
  }, [location.key]);

  useEffect(() => {
    if (!syncToUrl || !filtersInitialized) return;

    const nextFiltersParam = isSameUrlFilters(filters, initialFilters, defaultFilters)
      ? null
      : serializeUrlFilters(filters, defaultFilters, paramPrefix);
    const currentFiltersParam =
      urlFiltersQuery &&
      serializeUrlFilters(
        applyFilterHandler(
          defaultFilters,
          parseUrlFilters(urlFiltersQuery, defaultFilters, paramPrefix)
        ),
        defaultFilters,
        paramPrefix
      );
    if (nextFiltersParam === currentFiltersParam) return;

    setSearchParams((prev) => replaceUrlFilters(prev, paramPrefix, nextFiltersParam), {
      replace: true,
      state: location.state
    });
  }, [filters, initialFilters, filtersInitialized]);

  // A table can offer a different filter set per view; a value whose filter is gone can be neither
  // shown nor cleared, so it must stop narrowing the list.
  const filterKeys = Object.keys(defaultFilters || {}).join(',');
  useEffect(() => {
    const pickOffered = (current) => {
      if (!current) return current;
      const offered = Object.fromEntries(
        Object.entries(current).filter(([key]) => key in (defaultFilters || {}))
      );
      return Object.keys(offered).length === Object.keys(current).length ? current : offered;
    };
    setFilters(pickOffered);
    setInitialFilters(pickOffered);
  }, [filterKeys]);

  const isInitialFilter = useMemo(() => {
    return deepEqual(initialFilters, filters);
  }, [initialFilters, filters]);

  const resetFilters = useCallback(() => {
    const appliedFilters = applyFilterHandler(defaultFilters, null);
    setFilters(appliedFilters);
    setInitialFilters(appliedFilters);
  }, [defaultFilters]);

  const updateFilters = useCallback(
    (newFilter) => {
      const updatedFilters = applyFilterHandler(defaultFilters, newFilter);
      setFilters(updatedFilters);
      setInitialFilters(updatedFilters);
    },
    [setFilters, setInitialFilters]
  );

  const changeFilters = useCallback(
    async (updater, isSwitchItemView = false) => {
      setFilters((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        const isInitial = deepEqual(initialFilters, next);
        const isEditing = !!view && !isInitial && !isSwitchItemView;
        setIsEditingFilter(isEditing);
        return next;
      });
    },
    [initialFilters, setIsEditingFilter, view]
  );

  return {
    filters,
    initialFilters,
    filtersInitialized,
    isEditingFilter,
    isInitialFilter,
    resetFilters,
    updateFilters,
    changeFilters
  };
};
